import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function GiftLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/landing/${slug}`).then(({ data }) => {
      setData(data);
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp)).catch(() => {});
  }, [slug]);

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: data?.landing?.title || "Gift picks", url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => toast.success("Link copied — share anywhere!"));
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 animate-pulse">
        <div className="h-96 bg-[#F3EFEA] rounded-lg mb-10" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[0,1,2,3].map(i => <div key={i} className="aspect-[4/5] bg-[#F3EFEA] rounded-md" />)}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-display text-4xl mb-4">This gift page is no longer available</h2>
        <p className="text-sm text-[#9BA595] mb-6">It may have been retired for the season.</p>
        <Link to="/shop" className="text-sm underline underline-offset-8 hover:text-[#D27D60]">Explore the full shop</Link>
      </div>
    );
  }

  const { landing, products } = data;
  const theme = landing.theme_color || "#D27D60";

  return (
    <div data-testid="gift-landing-page">
      <section
        className="relative overflow-hidden border-b border-[#E3DCD2]"
        style={{ backgroundColor: `${theme}12` }}
      >
        {landing.hero_image && (
          <div className="absolute inset-0 opacity-20">
            <img src={landing.hero_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-widest-plus mb-4" style={{ color: theme }}>
              ✦ {landing.subtitle || "Curated gifts"}
            </div>
            <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95]" data-testid="gift-landing-title">
              {landing.title}
            </h1>
            {landing.intro && (
              <p className="text-base md:text-lg text-[#4a4a4a] mt-6 leading-relaxed max-w-xl">
                {landing.intro}
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={landing.cta_url || "/shop"}
                className="inline-flex items-center gap-3 text-[#FDFBF7] px-7 py-3.5 rounded-full text-sm uppercase tracking-widest-plus transition-transform hover:scale-105"
                style={{ backgroundColor: theme }}
                data-testid="gift-landing-cta"
              >
                {landing.cta_label || "Shop the collection"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <button
                onClick={share}
                className="inline-flex items-center gap-2 text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:opacity-70 transition-opacity"
                style={{ color: theme, textDecorationColor: `${theme}66` }}
                data-testid="gift-landing-share"
              >
                <Share2 className="w-4 h-4" strokeWidth={1.5} />
                Share this page
              </button>
              {whatsapp && (
                <a
                  href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi! I'd like help picking a gift from your ${landing.title} collection.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-widest-plus text-[#25D366] hover:opacity-70 transition-opacity"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  Help me pick
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-[11px] uppercase tracking-widest-plus mb-3" style={{ color: theme }}>✦ Handpicked</div>
        <h2 className="font-serif-display text-4xl md:text-5xl mb-10">The collection</h2>
        {products.length === 0 ? (
          <p className="text-sm text-[#9BA595]">No products in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
